import { Link, useNavigate } from "@tanstack/react-router";
import {
  Building2,
  Calendar,
  ChevronDown,
  ClipboardCheck,
  MapPin,
  Plus,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FieldOpsFilterRow } from "@/components/field-ops/field-ops-filters";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type InspectionChecklist,
  useChecklistTemplates,
  useCreateInspectionChecklist,
  useInspectionChecklists,
} from "@/hooks/use-inspections";
import { useProjects } from "@/hooks/use-projects";
import { useSession } from "@/lib/auth-client";
import { cn, formatDateOnlyLocal, localDateISOString } from "@/lib/utils";

type StatusFilter = "all" | "not_started" | "in_progress" | "completed" | "failed" | "void";

function statusVariant(
  status: InspectionChecklist["status"],
): "default" | "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "not_started":
      return "default";
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
    case "failed":
      return "destructive";
    case "void":
      return "secondary";
  }
}

function statusLabel(status: InspectionChecklist["status"]): string {
  switch (status) {
    case "not_started":
      return "Not Started";
    case "in_progress":
      return "In Progress";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "void":
      return "Void";
  }
}

function formatScheduled(dateStr: string | null): string {
  return formatDateOnlyLocal(dateStr, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatChecklistType(type: string): string {
  return type.replace(/_/g, " ");
}

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: "All statuses", value: "all" },
  { label: "Not Started", value: "not_started" },
  { label: "In Progress", value: "in_progress" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Void", value: "void" },
];

type InspectionsFilterProps = {
  filterProjectId: string;
  setFilterProjectId: (v: string) => void;
  scheduledFrom: string;
  setScheduledFrom: (v: string) => void;
  scheduledTo: string;
  setScheduledTo: (v: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (v: StatusFilter) => void;
  sortedProjects: Array<{ id: string; name: string }>;
  projectsLoading: boolean;
};

function buildInspectionActiveFilterLabels(props: InspectionsFilterProps): string[] {
  const labels: string[] = [];
  if (props.filterProjectId !== "all") {
    const project = props.sortedProjects.find((p) => p.id === props.filterProjectId);
    labels.push(project?.name ?? "Project");
  }
  if (props.scheduledFrom) labels.push(`From ${formatScheduled(props.scheduledFrom)}`);
  if (props.scheduledTo) labels.push(`To ${formatScheduled(props.scheduledTo)}`);
  if (props.statusFilter !== "all") {
    labels.push(statusLabel(props.statusFilter as InspectionChecklist["status"]));
  }
  return labels;
}

function InspectionsFilterFields({
  idPrefix,
  ...props
}: InspectionsFilterProps & { idPrefix: string }) {
  return (
    <>
      <FieldOpsFilterRow label="Project" htmlFor={`${idPrefix}-project`}>
        <Select
          value={props.filterProjectId}
          onValueChange={props.setFilterProjectId}
          disabled={props.projectsLoading}
        >
          <SelectTrigger id={`${idPrefix}-project`} className="min-h-[44px] w-full">
            <SelectValue
              placeholder={props.projectsLoading ? "Loading projects…" : "All projects"}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {props.sortedProjects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="From" htmlFor={`${idPrefix}-sched-from`}>
        <Input
          id={`${idPrefix}-sched-from`}
          type="date"
          value={props.scheduledFrom}
          onChange={(e) => props.setScheduledFrom(e.target.value)}
          className="min-h-[44px]"
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="To" htmlFor={`${idPrefix}-sched-to`}>
        <Input
          id={`${idPrefix}-sched-to`}
          type="date"
          value={props.scheduledTo}
          onChange={(e) => props.setScheduledTo(e.target.value)}
          className="min-h-[44px]"
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="Status" htmlFor={`${idPrefix}-status`}>
        <Select
          value={props.statusFilter}
          onValueChange={(v) => props.setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger id={`${idPrefix}-status`} className="min-h-[44px] w-full">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldOpsFilterRow>
    </>
  );
}

function InspectionsMobileFilters(props: InspectionsFilterProps) {
  const [open, setOpen] = useState(false);
  const activeLabels = useMemo(() => buildInspectionActiveFilterLabels(props), [props]);
  const activeCount = activeLabels.length;

  function clearFilters() {
    props.setFilterProjectId("all");
    props.setScheduledFrom("");
    props.setScheduledTo("");
    props.setStatusFilter("all");
  }

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-3 min-h-[44px] text-left sm:px-4"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="insp-mobile-filters-panel"
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Filters</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeCount}
              </Badge>
            )}
          </div>
          {!open && activeCount > 0 && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {activeLabels.join(" · ")}
            </p>
          )}
          {!open && activeCount === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Tap to refine the list</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <CardContent
          id="insp-mobile-filters-panel"
          className="space-y-2 border-t px-3 py-2 sm:px-4 sm:py-2.5"
        >
          <InspectionsFilterFields idPrefix="insp-filter" {...props} />
          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full min-h-[44px] text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all filters
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function InspectionsDesktopFilters(props: InspectionsFilterProps) {
  const activeCount = buildInspectionActiveFilterLabels(props).length;

  function clearFilters() {
    props.setFilterProjectId("all");
    props.setScheduledFrom("");
    props.setScheduledTo("");
    props.setStatusFilter("all");
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-3">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="insp-dt-project" className="text-xs text-muted-foreground">
              Project
            </Label>
            <Select
              value={props.filterProjectId}
              onValueChange={props.setFilterProjectId}
              disabled={props.projectsLoading}
            >
              <SelectTrigger id="insp-dt-project" className="h-9 w-full">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {props.sortedProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="insp-dt-from" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="insp-dt-from"
              type="date"
              value={props.scheduledFrom}
              onChange={(e) => props.setScheduledFrom(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="insp-dt-to" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="insp-dt-to"
              type="date"
              value={props.scheduledTo}
              onChange={(e) => props.setScheduledTo(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-end gap-3">
            <div className="space-y-1.5 min-w-0 flex-1">
              <Label htmlFor="insp-dt-status" className="text-xs text-muted-foreground">
                Status
              </Label>
              <Select
                value={props.statusFilter}
                onValueChange={(v) => props.setStatusFilter(v as StatusFilter)}
              >
                <SelectTrigger id="insp-dt-status" className="h-9 w-full">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 shrink-0"
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function InspectionsDesktopTableRow({
  checklist,
  projectName,
}: {
  checklist: InspectionChecklist;
  projectName: string | null;
}) {
  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell className="py-2.5 max-w-[220px]">
        <Link
          to="/inspections/$id"
          params={{ id: checklist.id }}
          className="font-medium line-clamp-2 hover:underline"
        >
          {checklist.title}
        </Link>
      </TableCell>
      <TableCell className="py-2.5 max-w-[160px] truncate text-muted-foreground">
        {projectName ?? "—"}
      </TableCell>
      <TableCell className="py-2.5 capitalize text-muted-foreground text-sm">
        {formatChecklistType(checklist.checklistType)}
      </TableCell>
      <TableCell className="py-2.5">
        <Badge variant={statusVariant(checklist.status)}>{statusLabel(checklist.status)}</Badge>
      </TableCell>
      <TableCell className="py-2.5 whitespace-nowrap text-muted-foreground text-sm">
        {checklist.scheduledDate ? formatScheduled(checklist.scheduledDate) : "—"}
      </TableCell>
      <TableCell className="py-2.5 max-w-[120px] truncate text-muted-foreground text-sm">
        {checklist.location ?? "—"}
      </TableCell>
      <TableCell className="py-2.5 text-sm">
        {checklist.inspectionPassed === null ? (
          <span className="text-muted-foreground">—</span>
        ) : checklist.inspectionPassed ? (
          <span className="text-green-600 font-medium">Passed</span>
        ) : (
          <span className="text-destructive font-medium">Failed</span>
        )}
      </TableCell>
    </TableRow>
  );
}

function InspectionsDesktopTable({
  items,
  projectNameById,
}: {
  items: InspectionChecklist[];
  projectNameById: Map<string, string>;
}) {
  const sorted = [...items].sort((a, b) => {
    const da = a.scheduledDate ?? "";
    const db = b.scheduledDate ?? "";
    if (da !== db) return db.localeCompare(da);
    return b.createdAt.localeCompare(a.createdAt);
  });

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Title</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((checklist) => (
            <InspectionsDesktopTableRow
              key={checklist.id}
              checklist={checklist}
              projectName={projectNameById.get(checklist.projectId) ?? null}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ChecklistCard({
  checklist,
  projectName,
}: {
  checklist: InspectionChecklist;
  projectName: string | null;
}) {
  return (
    <Link to="/inspections/$id" params={{ id: checklist.id }} className="block">
      <Card className="gap-0 py-0 shadow-sm hover:bg-accent/40 transition-colors cursor-pointer">
        <CardContent className="space-y-2 p-3 sm:p-3.5">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ClipboardCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="font-medium text-sm truncate">{checklist.title}</span>
            </div>
            <Badge variant={statusVariant(checklist.status)} className="shrink-0">
              {statusLabel(checklist.status)}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {projectName ? (
              <span className="flex items-center gap-1 font-medium text-foreground/80">
                <Building2 className="h-3 w-3 shrink-0" />
                {projectName}
              </span>
            ) : null}
            <span className="capitalize">{checklist.checklistType.replace(/_/g, " ")}</span>

            {checklist.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {checklist.location}
              </span>
            )}

            {checklist.scheduledDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatScheduled(checklist.scheduledDate)}
              </span>
            )}
          </div>

          {checklist.inspectionPassed !== null && (
            <div className="flex items-center gap-1.5 text-xs">
              <span
                className={
                  checklist.inspectionPassed
                    ? "text-green-600 font-medium"
                    : "text-destructive font-medium"
                }
              >
                {checklist.inspectionPassed ? "Passed" : "Failed"}
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function NewInspectionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [projectId, setProjectId] = useState("");
  const [checklistTemplateId, setChecklistTemplateId] = useState("");
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [scheduledDate, setScheduledDate] = useState(() => localDateISOString());

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: templates = [], isLoading: templatesLoading } = useChecklistTemplates();
  const create = useCreateInspectionChecklist();

  const sortedProjects = [...projects].sort((a, b) => a.name.localeCompare(b.name));
  const sortedTemplates = [...templates].sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    if (!open) return;
    setProjectId("");
    setChecklistTemplateId("");
    setTitle("");
    setLocation("");
    setScheduledDate(localDateISOString());
  }, [open]);

  const canSubmit =
    Boolean(projectId) &&
    Boolean(checklistTemplateId) &&
    Boolean(title.trim()) &&
    Boolean(scheduledDate) &&
    !create.isPending &&
    !projectsLoading &&
    !templatesLoading;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        projectId,
        checklistTemplateId,
        title: title.trim(),
        ...(location.trim() ? { location: location.trim() } : {}),
        scheduledDate,
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          void navigate({ to: "/inspections/$id", params: { id: data.id } });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="min-w-0">
          <DialogHeader>
            <DialogTitle>New Inspection</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="ni-project">Project</Label>
              <Select
                value={projectId || undefined}
                onValueChange={setProjectId}
                disabled={projectsLoading || sortedProjects.length === 0}
              >
                <SelectTrigger id="ni-project" className="min-h-[44px]">
                  <SelectValue
                    placeholder={projectsLoading ? "Loading projects…" : "Select a project"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortedProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ni-template">Template</Label>
              <Select
                value={checklistTemplateId || undefined}
                onValueChange={(id) => {
                  setChecklistTemplateId(id);
                  const t = sortedTemplates.find((x) => x.id === id);
                  if (t) setTitle(t.name);
                }}
                disabled={templatesLoading || sortedTemplates.length === 0}
              >
                <SelectTrigger id="ni-template" className="min-h-[44px]">
                  <SelectValue
                    placeholder={templatesLoading ? "Loading templates…" : "Select a template"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortedTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!templatesLoading && sortedTemplates.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {session?.user?.role === "admin" ? (
                    <>
                      Add checklist templates in{" "}
                      <Link
                        to="/admin/config/$section"
                        params={{ section: "checklist-templates" }}
                        className="text-primary underline underline-offset-2 font-medium"
                      >
                        Admin → Checklist templates
                      </Link>{" "}
                      before creating an inspection.
                    </>
                  ) : (
                    <>Ask an admin to add checklist templates before creating an inspection.</>
                  )}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ni-title">Title</Label>
              <Input
                id="ni-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Inspection title"
                className="min-h-[44px]"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ni-location">Location (optional)</Label>
              <Input
                id="ni-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Floor 3, Unit 12"
                className="min-h-[44px]"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ni-date">Scheduled date</Label>
              <Input
                id="ni-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className="min-h-[44px]"
                required
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" className="min-h-[44px]" disabled={!canSubmit}>
              {create.isPending ? "Creating…" : "Create Inspection"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function InspectionsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [filterProjectId, setFilterProjectId] = useState<string>("all");
  const [scheduledFrom, setScheduledFrom] = useState("");
  const [scheduledTo, setScheduledTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: session } = useSession();

  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data, isLoading, isError, error, refetch } = useInspectionChecklists(
    filterProjectId !== "all" ? filterProjectId : undefined,
  );
  const canCreateInspection = session?.user?.role !== "contractor";

  const items = data ?? [];
  let filtered = statusFilter === "all" ? items : items.filter((i) => i.status === statusFilter);
  if (scheduledFrom) {
    filtered = filtered.filter((c) => c.scheduledDate != null && c.scheduledDate >= scheduledFrom);
  }
  if (scheduledTo) {
    filtered = filtered.filter((c) => c.scheduledDate != null && c.scheduledDate <= scheduledTo);
  }

  const sortedProjects = [...projects].sort((a, b) => a.name.localeCompare(b.name));

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  const filterProps: InspectionsFilterProps = {
    filterProjectId,
    setFilterProjectId,
    scheduledFrom,
    setScheduledFrom,
    scheduledTo,
    setScheduledTo,
    statusFilter,
    setStatusFilter,
    sortedProjects,
    projectsLoading,
  };

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Inspections</h1>
        {canCreateInspection && (
          <Button className="min-h-[44px] lg:min-h-9" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Inspection
          </Button>
        )}
      </div>

      <div className="hidden lg:block">
        <InspectionsDesktopFilters {...filterProps} />
      </div>

      <div className="lg:hidden">
        <InspectionsMobileFilters {...filterProps} />
      </div>

      {isLoading && (
        <>
          <div className="space-y-2 lg:hidden">
            {[1, 2, 3].map((n) => (
              <Card key={n}>
                <CardContent className="space-y-2 p-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-5 w-20" />
                  </div>
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="hidden lg:block">
            <Skeleton className="h-80 w-full rounded-lg" />
          </div>
        </>
      )}

      {isError && (
        <ErrorState
          message={error?.message ?? "Failed to load inspections"}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && filtered.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ClipboardCheck className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No inspections found</p>
            <p className="text-sm mt-1">
              {statusFilter === "all"
                ? filterProjectId === "all"
                  ? "Create your first inspection to get started."
                  : "No inspections for this project."
                : `No inspections with status "${statusLabel(statusFilter as InspectionChecklist["status"])}".`}
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && filtered.length > 0 && (
        <>
          <div className="space-y-2 lg:hidden">
            {filtered.map((checklist) => (
              <ChecklistCard
                key={checklist.id}
                checklist={checklist}
                projectName={projectNameById.get(checklist.projectId) ?? null}
              />
            ))}
          </div>
          <div className="hidden lg:block">
            <InspectionsDesktopTable items={filtered} projectNameById={projectNameById} />
          </div>
        </>
      )}

      {canCreateInspection && (
        <NewInspectionDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </div>
  );
}
